// useFlashOnChange — die sichtbare Propagation (Masterplan §4:
// "geänderte Zahl pulst 1× dezent").
//
// Beobachtet einen Zahlenwert und meldet einen kurzen Flash (~650ms), wenn
// er sich ÄNDERT — nicht beim ersten Mount. Richtung (up/down) steuert die
// Akzentfarbe (grün = gestiegen, clay = gefallen). Rein CSS-getriggert, kein
// rAF — bleibt im Motion-Budget (Masterplan §6). Query-Refetch oder ein
// manuell gespeicherter Saldo lässt so die ganze Seite kurz "atmen", ohne
// dass eine Zahl aus dem Nichts springt.

import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";

export type FlashDir = "up" | "down";

export interface FlashState {
  flash: boolean;
  dir: FlashDir | null;
}

const FLASH_MS = 650;

export function useFlashOnChange(value: number | null | undefined): FlashState {
  const prev = useRef<number | null | undefined>(value);
  const [state, setState] = useState<FlashState>({ flash: false, dir: null });

  useEffect(() => {
    const before = prev.current;
    prev.current = value;
    // Nur bei echter Änderung zwischen zwei gültigen Werten flashen.
    if (before == null || value == null || value === before) return;
    setState({ flash: true, dir: value > before ? "up" : "down" });
    const t = window.setTimeout(() => setState((s) => ({ ...s, flash: false })), FLASH_MS);
    return () => window.clearTimeout(t);
  }, [value]);

  return state;
}

/** Tailwind-/CSS-Klasse für den Flash (siehe index.css: value-flash-*). */
export function flashClass({ flash, dir }: FlashState): string {
  if (!flash || dir == null) return "";
  return dir === "up" ? "value-flash-up" : "value-flash-down";
}

/** Flow-Kaskade: verzögert den Flash, damit eine Reihe von Zahlen bei einem
 *  gemeinsamen Refetch nacheinander aufleuchtet (Welle statt Blitz).
 *  delayMs nur anwenden, solange geflasht wird. */
export function flashStyle(state: FlashState, delayMs: number): CSSProperties {
  return state.flash && delayMs > 0 ? { animationDelay: `${delayMs}ms` } : {};
}
